/**
 * Formatting & locale utilities shared across screens.
 *
 * (Demo / sample data has been removed: the app shows real API data and honest
 * loading / empty / error / offline states instead of fabricated content.)
 */
import type { Step } from "@tebiki/shared";

/** Lightweight keyframe candidate type used by the picker (augments Step). */
export interface KeyframeCandidate {
  timecodeMs: number;
  label: string;
  recommended?: boolean;
}
export type StepWithCandidates = Step & { candidates?: KeyframeCandidate[] };

/** Format milliseconds as MM:SS. */
export function mmss(ms: number): string {
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

/** Target locales offered alongside the JA master (multilingual screen). */
/**
 * Offered translation targets (order = display order). The API accepts any BCP47-ish locale,
 * so adding a language here is enough. Set chosen for the industries TEBIKI serves —
 * 外国人スタッフの多い飲食・小売・製造の主要言語.
 */
export const TARGET_LOCALES = ["en", "zh", "zh-TW", "ko", "vi", "id", "th", "ne", "my", "pt", "es", "tl"] as const;

const LOCALE_LABEL: Record<string, { flag: string; name: string }> = {
  ja: { flag: "🇯🇵", name: "日本語（原文）" },
  en: { flag: "🇬🇧", name: "English" },
  zh: { flag: "🇨🇳", name: "中文（简体）" },
  "zh-TW": { flag: "🇹🇼", name: "中文（繁體）" },
  ko: { flag: "🇰🇷", name: "한국어" },
  vi: { flag: "🇻🇳", name: "Tiếng Việt" },
  id: { flag: "🇮🇩", name: "Bahasa Indonesia" },
  th: { flag: "🇹🇭", name: "ไทย" },
  ne: { flag: "🇳🇵", name: "नेपाली" },
  my: { flag: "🇲🇲", name: "မြန်မာ" },
  pt: { flag: "🇧🇷", name: "Português" },
  es: { flag: "🇪🇸", name: "Español" },
  tl: { flag: "🇵🇭", name: "Filipino" },
};

export function localeLabel(locale: string): { flag: string; name: string } {
  return LOCALE_LABEL[locale] ?? { flag: "🌐", name: locale.toUpperCase() };
}
